/**
 * Unified Activity Tracker
 * 
 * Centralized activity tracking service that manages consistent timestamps
 * across all notification and recovery systems.
 * 
 * Key Features:
 * - Unified timestamp management between frontend and backend
 * - Activity gap calculation for recovery system orchestration
 * - Automatic sync with backend session cache
 * - Consistent parameters for all recovery systems
 */

import { apiClient } from './api';

interface ActivityState {
  lastUserActivity: string;
  sessionStartTime: number;
  lastSeenEventId?: string;
  isConnected: boolean;
}

interface RecoveryParams {
  since_event_id?: string;
  since_timestamp?: number;
  last_user_activity?: string;
  lookback_window_ms?: number;
  activity_gap_ms?: number;
}

export class UnifiedActivityTracker {
  private static instance: UnifiedActivityTracker;
  private state: ActivityState;
  private syncTimeoutId: NodeJS.Timeout | null = null;
  private readonly SYNC_DEBOUNCE_MS = 5000; // 5 seconds
  private readonly ACTIVITY_THROTTLE_MS = 30000; // 30 seconds

  private constructor() {
    this.state = {
      lastUserActivity: localStorage.getItem('lastUserActivity') || new Date().toISOString(),
      sessionStartTime: Date.now(),
      lastSeenEventId: localStorage.getItem('lastSeenEventId') || undefined,
      isConnected: false
    };

    // Initialize activity tracking on creation
    this.updateActivity();
    this.setupActivityListeners();
  }

  public static getInstance(): UnifiedActivityTracker {
    if (!UnifiedActivityTracker.instance) {
      UnifiedActivityTracker.instance = new UnifiedActivityTracker();
    }
    return UnifiedActivityTracker.instance;
  }

  /**
   * Update user activity timestamp and trigger sync
   */
  public updateActivity(): void {
    const now = new Date().toISOString();
    const previousActivity = this.state.lastUserActivity;
    
    // Check if enough time has passed since last update (throttling)
    const timeSinceLastUpdate = Date.now() - new Date(previousActivity).getTime();
    if (timeSinceLastUpdate < this.ACTIVITY_THROTTLE_MS) {
      return; // Skip update if too recent
    }

    this.state.lastUserActivity = now;
    localStorage.setItem('lastUserActivity', now);
    
    console.log(`🕐 Activity tracker: Updated user activity (${timeSinceLastUpdate}ms since last update)`);
    
    // Debounced sync with backend
    this.scheduleSync();
  }

  /**
   * Update connection status
   */
  public setConnectionStatus(connected: boolean): void {
    this.state.isConnected = connected;
    
    if (connected) {
      // Reset session start time on successful connection
      this.state.sessionStartTime = Date.now();
    }
  }

  /**
   * Update last seen event ID
   */
  public updateLastSeenEventId(eventId: string): void {
    this.state.lastSeenEventId = eventId;
    localStorage.setItem('lastSeenEventId', eventId);
    console.log(`📌 Activity tracker: Updated last seen event ID: ${eventId}`);
  }

  /**
   * Calculate activity gap in milliseconds
   */
  public getActivityGap(): number {
    const lastActivity = new Date(this.state.lastUserActivity).getTime();
    return Date.now() - lastActivity;
  }

  /**
   * Calculate session gap from stored session end time (for recovery purposes)
   * This uses the session cache timestamp, not the current activity timestamp
   */
  public getSessionGap(): number {
    // Try to get session end time from localStorage (set by session gap detection)
    const sessionEndStr = localStorage.getItem('lastSessionEnd');
    if (sessionEndStr) {
      const sessionEndTime = parseInt(sessionEndStr);
      return Date.now() - sessionEndTime;
    }
    
    // Fallback to activity gap if no session end time is available
    return this.getActivityGap();
  }

  /**
   * Get consistent recovery parameters for all notification systems
   */
  public getRecoveryParams(): RecoveryParams {
    const params: RecoveryParams = {};
    
    if (this.state.lastSeenEventId) {
      params.since_event_id = this.state.lastSeenEventId;
    } else {
      // Use session gap for recovery (not activity gap which gets updated on app open)
      const sessionGap = this.getSessionGap();
      
      // Use session gap + 20% buffer, minimum 15 minutes, maximum 24 hours
      const lookbackWindow = Math.min(
        Math.max(sessionGap * 1.2, 15 * 60 * 1000), 
        24 * 60 * 60 * 1000
      );
      
      params.since_timestamp = Date.now() - lookbackWindow;
      params.lookback_window_ms = lookbackWindow;
      params.activity_gap_ms = sessionGap; // Use session gap, not activity gap
      
      console.log(`🔍 Activity tracker: Recovery params - session gap ${(sessionGap / 60000).toFixed(1)}min, lookback ${(lookbackWindow / 60000).toFixed(1)}min`);
    }
    
    if (this.state.lastUserActivity) {
      params.last_user_activity = this.state.lastUserActivity;
    }
    
    return params;
  }

  /**
   * Get current activity state
   */
  public getState(): Readonly<ActivityState> {
    return { ...this.state };
  }

  /**
   * Determine recovery scenario based on activity gap
   */
  public getRecoveryScenario(): 'real-time' | 'short-gap' | 'session-gap' | 'long-gap' {
    // Use session gap for recovery scenario determination
    const gapMs = this.getSessionGap();
    
    if (gapMs < 30 * 1000) {
      return 'real-time'; // < 30 seconds
    } else if (gapMs < 30 * 60 * 1000) {
      return 'short-gap'; // < 30 minutes
    } else if (gapMs < 6 * 60 * 60 * 1000) {
      return 'session-gap'; // < 6 hours
    } else {
      return 'long-gap'; // >= 6 hours
    }
  }

  /**
   * Reset activity state (e.g., on logout)
   */
  public reset(): void {
    this.state = {
      lastUserActivity: new Date().toISOString(),
      sessionStartTime: Date.now(),
      lastSeenEventId: undefined,
      isConnected: false
    };
    
    localStorage.removeItem('lastUserActivity');
    localStorage.removeItem('lastSeenEventId');
    
    console.log('🔄 Activity tracker: State reset');
  }

  /**
   * Schedule debounced sync with backend
   */
  private scheduleSync(): void {
    // Clear existing timeout
    if (this.syncTimeoutId) {
      clearTimeout(this.syncTimeoutId);
    }
    
    // Schedule new sync
    this.syncTimeoutId = setTimeout(() => {
      this.syncWithSessionCache();
    }, this.SYNC_DEBOUNCE_MS);
  }

  /**
   * Sync activity state with backend session cache
   */
  private async syncWithSessionCache(): Promise<void> {
    try {
      // Check if user is authenticated before making API calls
      const { useAuthStore } = await import('../store/authStore');
      const { accessToken, isApiAuthReady } = useAuthStore.getState();
      
      if (!accessToken || !isApiAuthReady) {
        console.log('🛑 Activity tracker: Sync skipped - user not authenticated');
        return;
      }

      const response = await apiClient.post('/session/sync-activity', {
        lastUserActivity: this.state.lastUserActivity,
        sessionStartTime: this.state.sessionStartTime,
        lastSeenEventId: this.state.lastSeenEventId
      });
      
      if (response.data?.synced) {
        console.log('✅ Activity tracker: Synced with backend session cache');
      } else {
        console.warn('⚠️ Activity tracker: Backend sync failed');
      }
    } catch (error) {
      console.warn('⚠️ Activity tracker: Backend sync error:', error);
      // Don't throw - sync failures shouldn't break the app
    }
  }

  /**
   * Setup activity event listeners
   */
  private setupActivityListeners(): void {
    const events = ['click', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    
    const throttledUpdate = () => {
      this.updateActivity();
    };

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, throttledUpdate, { passive: true });
    });

    // Handle visibility changes
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        this.updateActivity();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Handle page unload
    const handleBeforeUnload = () => {
      // Mark session end
      localStorage.setItem('sessionEndTime', new Date().toISOString());
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    console.log('🎧 Activity tracker: Event listeners setup complete');
  }
}

// Export singleton instance
export const unifiedActivityTracker = UnifiedActivityTracker.getInstance();

// Export factory function for dependency injection
export const createUnifiedActivityTracker = (): UnifiedActivityTracker => {
  return UnifiedActivityTracker.getInstance();
};